import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSubscriberLists } from '@/features/subscribers';
import { queryKeys } from '@/shared/api/queryKeys';
import { approveDraft, fetchDrafts, rejectDraft, type DraftParams } from '../api/draftsApi';

const SENDING_POLL_MS = 2000;

const BADGE_POLL_MS = 60_000;

/** How many drafts wait for an analyst's decision: the number beside Approvals in the menu. */
export function useWaitingDraftCount(enabled: boolean) {
  return useDraftCount('pending', enabled);
}

/** How many drafts have a status, for the Waiting, Sent and Rejected tabs. */
export function useDraftCount(status: 'pending' | 'sent' | 'rejected', enabled = true) {
  const params = { page: 1, pageSize: 1, status };
  return useQuery({
    queryKey: queryKeys.drafts.list(params),
    queryFn: ({ signal }) => fetchDrafts(params, signal),
    enabled,
    refetchInterval: BADGE_POLL_MS,
    select: (p) => p.total,
  });
}

export function useDraftsQuery(params: DraftParams) {
  return useQuery({
    queryKey: queryKeys.drafts.list(params),
    queryFn: ({ signal }) => fetchDrafts(params, signal),
    placeholderData: keepPreviousData,
    // Delivery statuses arrive by webhook; poll while any message on screen is still in flight.
    refetchInterval: (q) =>
      q.state.data?.items.some((d) => d.status === 'sending' || d.deliveries.some((x) => x.status === 'queued' || x.status === 'sent'))
        ? SENDING_POLL_MS
        : false,
  });
}

export function useListsQuery() {
  return useQuery({ queryKey: queryKeys.subscribers.lists(), queryFn: ({ signal }) => fetchSubscriberLists(signal) });
}

function useInvalidateDrafts() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.drafts.all });
    void qc.invalidateQueries({ queryKey: queryKeys.audit.all });
  };
}

export function useApproveDraft() {
  const invalidate = useInvalidateDrafts();
  return useMutation({
    mutationFn: ({ id, text, listId }: { id: string; text: string; listId: string }) => approveDraft(id, { text, listId }),
    onSuccess: invalidate,
  });
}

export function useRejectDraft() {
  const invalidate = useInvalidateDrafts();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectDraft(id, { reason }),
    onSuccess: invalidate,
  });
}
